import { useEffect, useRef, useState } from 'react';
import { Modal, View, Image, ScrollView, TouchableOpacity, Text, Dimensions } from 'react-native';

/**
 * Shared full-screen, swipeable image lightbox for the driver app.
 * Usage: keep `images: string[]` + an `index: number | null` in state, render
 *   <ImageLightbox images={imgs} index={open} onClose={() => setOpen(null)} />
 * Open it by setting index to the tapped photo's position. Swipe left/right to
 * scroll through all images; tap × to close. Each photo can be ROTATED 90° at a
 * time and double-tapped (or the ⤢ button) to toggle a 2× zoom.
 */
export default function ImageLightbox({ images, index, onClose }: { images: string[]; index: number | null; onClose: () => void }) {
  const [i, setI] = useState(index ?? 0);
  const [rot, setRot] = useState<Record<number, number>>({});   // per-image rotation (deg)
  const [zoom, setZoom] = useState<Record<number, number>>({}); // per-image scale
  const ref = useRef<ScrollView>(null);
  const W = Dimensions.get('window').width;
  const H = Dimensions.get('window').height;

  useEffect(() => {
    if (index != null) {
      setI(index);
      setRot({});
      setZoom({});
      requestAnimationFrame(() => ref.current?.scrollTo({ x: index * W, animated: false }));
    }
  }, [index, W]);

  if (index == null || !images.length) return null;

  const rotate = () => setRot((r) => ({ ...r, [i]: ((r[i] || 0) + 90) % 360 }));
  const toggleZoom = (k: number) => setZoom((z) => ({ ...z, [k]: (z[k] || 1) > 1 ? 1 : 2 }));

  const ctrlBtn = { width: 44, height: 44, borderRadius: 22, backgroundColor: 'rgba(255,255,255,0.18)', justifyContent: 'center' as const, alignItems: 'center' as const };

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.94)' }}>
        <ScrollView
          ref={ref}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          contentOffset={{ x: (index ?? 0) * W, y: 0 }}
          onMomentumScrollEnd={(e) => setI(Math.round(e.nativeEvent.contentOffset.x / W))}
        >
          {images.map((u, k) => {
            const deg = rot[k] || 0;
            const scale = zoom[k] || 1;
            const turned = deg % 180 !== 0;
            // When turned 90°/270°, swap the box dims so the photo still fits the screen.
            return (
              <TouchableOpacity key={k} activeOpacity={1} onPress={() => toggleZoom(k)} style={{ width: W, height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <Image
                  source={{ uri: u }}
                  style={{ width: turned ? H : W, height: turned ? W : H, transform: [{ rotate: `${deg}deg` }, { scale }] }}
                  resizeMode="contain"
                />
              </TouchableOpacity>
            );
          })}
        </ScrollView>

        {/* Close */}
        <TouchableOpacity onPress={onClose} style={{ position: 'absolute', top: 48, right: 20, ...ctrlBtn }}>
          <Text style={{ color: '#fff', fontSize: 24, lineHeight: 26 }}>×</Text>
        </TouchableOpacity>

        {/* Rotate + zoom toolbar */}
        <View style={{ position: 'absolute', top: 48, left: 20, flexDirection: 'row', gap: 10 }}>
          <TouchableOpacity onPress={rotate} style={ctrlBtn} accessibilityLabel="Rotate">
            <Text style={{ color: '#fff', fontSize: 22, lineHeight: 24 }}>↻</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => toggleZoom(i)} style={ctrlBtn} accessibilityLabel="Zoom">
            <Text style={{ color: '#fff', fontSize: 18, lineHeight: 20 }}>⤢</Text>
          </TouchableOpacity>
        </View>

        {images.length > 1 && (
          <View style={{ position: 'absolute', bottom: 42, alignSelf: 'center', backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: 14, paddingHorizontal: 13, paddingVertical: 5 }}>
            <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>{i + 1} / {images.length}</Text>
          </View>
        )}
      </View>
    </Modal>
  );
}
